import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import FormInput from 'components/common/form-input';
import Tooltip from 'components/layout/tooltip';
import { exposureOptionsWithHorizontal } from 'data/constants';
import { SizingItemsType, SkylightItemType } from 'data/types';
import { useState } from 'react';
import { isNotEmpty } from 'utils/helpers';
import {
  getValidSizingAttributeOptions,
  isValidSizingItem
} from 'utils/sizing';

type SkylightProps = {
  skylight: SkylightItemType;
  updateFn: (skylightId: string, updates: Partial<SkylightItemType>) => void;
  duplicateFn: (skylightId: string) => void;
  deleteFn: (skylightId: string) => void;
  updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
};

export default function SkylightComponent({
  skylight,
  updateFn,
  duplicateFn,
  deleteFn,
  updateDefaultSizingFn
}: SkylightProps) {
  const defaultValues = useAppSelector(
    (state) => state.data.defaultSizingItems.skylight
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [animate] = useAutoAnimate({ duration: 100 });
  const [deleteQueued, setDeleteQueued] = useState(false);
  const sizingItemType = 'skylights';
  const isModified = Object.keys(defaultValues)
    .filter((key) => isNotEmpty(defaultValues[key]))
    .some((key) => skylight[key] !== defaultValues[key]);

  const handleUpdateWithValidation = (changes: Partial<SkylightItemType>) => {
    const newSkylight: SkylightItemType = {
      ...skylight,
      ...changes
    };
    const isValid = isValidSizingItem(sizingItemType, newSkylight);

    updateFn(skylight.uniqueId, {
      ...newSkylight,
      valid: isValid
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions((prev) => !prev);
  };

  const handleDuplicateClick = () => {
    duplicateFn(skylight.uniqueId);
  };

  const handleDeleteClick = () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }

    deleteFn(skylight.uniqueId);
    setDeleteQueued(false);
  };

  const setAsDefault = () => {
    updateDefaultSizingFn({
      skylight: {
        ...skylight,
        uniqueId: undefined,
        valid: undefined,
        exposure: undefined
      }
    });
  };

  const classes = clsx(
    'flex w-full flex-col divide-y divide-gray-300 rounded-xl bg-gray-200 px-5 border-2',
    skylight.valid ? 'border-gray-200' : 'border-red-500'
  );

  return (
    <div key={skylight.uniqueId} className={classes} ref={animate}>
      <div className='flex min-h-[54px] items-center gap-5'>
        <div className='font-semibold'>{skylight.exposure} Skylight</div>
        <div className='w-[130px]'>
          <FormInput
            value={skylight.length}
            placeholder='Length (ft)'
            prefix='Length: '
            type='number'
            suffix=' ft'
            min={1}
            onChange={(value) => handleUpdateWithValidation({ length: value })}
            required
          />
        </div>
        <div className='w-[130px]'>
          <FormInput
            value={skylight.width}
            placeholder='Width (ft)'
            prefix='Width: '
            type='number'
            suffix=' ft'
            min={1}
            onChange={(value) => handleUpdateWithValidation({ width: value })}
            required
          />
        </div>
        {!skylight.valid && (
          <span className='text-red-500'>Invalid Attributes!</span>
        )}
        <div className='ml-auto flex items-center gap-2'>
          <button
            className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
            type='button'
            onClick={toggleAdvancedOptions}
          >
            <span>Advanced</span>
            {showAdvancedOptions ? (
              <ChevronUpIcon className='h-5 w-5' />
            ) : (
              <ChevronDownIcon className='h-5 w-5' />
            )}
            <Tooltip
              text={
                showAdvancedOptions
                  ? 'Hide Advanced Options'
                  : 'Show Advanced Options'
              }
            />
          </button>
          <button
            className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
            type='button'
            onClick={handleDuplicateClick}
          >
            <DocumentDuplicateIcon className='h-5 w-5' />
            <Tooltip text='Duplicate Skylight' />
          </button>
          <button
            className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
            type='button'
            onClick={handleDeleteClick}
            onMouseLeave={() => setDeleteQueued(false)}
          >
            <TrashIcon className='h-5 w-5' />
            <Tooltip
              text={deleteQueued ? 'Click Again to Confirm' : 'Delete Skylight'}
            />
          </button>
        </div>
      </div>
      {showAdvancedOptions && (
        <div className='flex flex-wrap items-center gap-x-5 gap-y-3 py-3'>
          <FormInput
            value={skylight.exposure}
            label='Exposure'
            type='select'
            options={exposureOptionsWithHorizontal}
            onChange={(value) => {
              handleUpdateWithValidation({
                exposure: value
              });
            }}
            required
            preventDeselection
          />
          <FormInput
            value={skylight.panes}
            label='Panes'
            type='select'
            options={getValidSizingAttributeOptions(
              sizingItemType,
              skylight,
              'panes'
            )}
            onChange={(value) => {
              handleUpdateWithValidation({
                panes: value
              });
            }}
            required
            preventDeselection
          />
          <FormInput
            value={skylight.sash}
            label='Sash'
            type='select'
            options={getValidSizingAttributeOptions(
              sizingItemType,
              skylight,
              'sash'
            )}
            onChange={(value) => {
              handleUpdateWithValidation({
                sash: value
              });
            }}
            required
            preventDeselection
          />
          <FormInput
            value={skylight.constructionType}
            label='Construction Type'
            type='select'
            options={getValidSizingAttributeOptions(
              sizingItemType,
              skylight,
              'constructionType'
            )}
            onChange={(value) => {
              handleUpdateWithValidation({
                constructionType: value
              });
            }}
            required
            preventDeselection
          />
          <FormInput
            value={skylight.glazingPanelInclination}
            label='Glazing Panel Inclination'
            type='select'
            options={[
              '30 Degrees From Horizontal',
              '45 Degrees From Horizontal',
              '60 Degrees From Horizontal',
              'Horizontal'
            ]}
            onChange={(value) => {
              handleUpdateWithValidation({
                glazingPanelInclination: value
              });
            }}
            preventDeselection
            required
          />
          {isModified && (
            <div className='flex w-full justify-end'>
              <button
                className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
                type='button'
                onClick={setAsDefault}
              >
                <span>Set as Default</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
